import { FileTextOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Collapse, Empty, Flex, Typography } from "antd"
import type { ColumnsType } from "antd/es/table"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { EntityTable } from "../../../components/EntityTable/EntityTable.tsx"
import { RowActionsMenu } from "../../../components/EntityTable/RowActionsMenu.tsx"
import {
  DEFAULT_TAB,
  TABLE_SURFACES
} from "../../../components/EntityTable/surfaces.ts"
import { TableResultCount } from "../../../components/Table/TableResultCount.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useScrolledTable } from "../../../hooks/use-scrolled-table.ts"
import { useTableContentHeight } from "../../../hooks/use-table-content-height.ts"
import { selectActiveBranchId } from "../../Branches/redux/branches.slice.ts"
import {
  type RowAction,
  primaryActionOf,
  queryForView
} from "../helpers/manager-views.ts"
import {
  activeFilterCount,
  queryFromFilters
} from "../helpers/tracking-query.ts"
import { useCanCountersign } from "../hooks/use-countersign.ts"
import { useEnvelopeLifecycle } from "../hooks/use-envelope-lifecycle.tsx"
import { useTrackingFilters } from "../hooks/use-tracking-filters.ts"
import { useTrackingList } from "../hooks/use-tracking-list.ts"
import {
  useTrackingFilterOptions,
  useTrackingStats,
  useTrackingSummary
} from "../hooks/use-tracking-overview.ts"
import { useViewCounts } from "../hooks/use-view-counts.ts"
import {
  EMPTY_FILTERS,
  MANAGER_VIEWS,
  type ManagerView,
  type TrackedEnvelope,
  type TrackingFilters
} from "../types/tracking.types.ts"
import { EnvelopeActivityDrawer } from "./components/EnvelopeActivityDrawer.tsx"
import { SignedDocumentDrawer } from "./components/SignedDocumentDrawer.tsx"
import { TrackingFiltersBar } from "./components/TrackingFiltersBar.tsx"
import { TrackingKpiRow } from "./components/TrackingKpiRow.tsx"
import { TrackingSummaryStrip } from "./components/TrackingSummaryStrip.tsx"
import { WelcomeBanner } from "./components/WelcomeBanner.tsx"
import { useTrackingColumns } from "./components/tracking-columns.tsx"

const { Text, Title } = Typography

function ViewTabs({
  views,
  current,
  counts,
  onChange
}: {
  views: readonly ManagerView[]
  current: ManagerView
  counts: Partial<Record<ManagerView, number>>
  onChange: (view: ManagerView) => void
}) {
  const { t } = useTranslation()
  return (
    <div
      role={"tablist"}
      aria-label={t("esign.manager.viewsLabel")}
      className={
        "flex gap-6 overflow-x-auto border-0 border-b border-solid border-b-slate-200 px-4"
      }
    >
      {views.map((view) => {
        const selected = view === current
        const count = counts[view]
        return (
          <button
            key={view}
            type={"button"}
            role={"tab"}
            aria-selected={selected}
            aria-label={
              count !== undefined
                ? `${t(`esign.manager.views.${view}`)} · ${count}`
                : undefined
            }
            data-testid={`manager-view-${view}`}
            onClick={() => onChange(view)}
            className={`-mb-px flex shrink-0 cursor-pointer items-center gap-1.5 border-0 border-b-2 border-solid bg-transparent px-0 py-3 text-sm ${
              selected
                ? "border-b-primary font-semibold text-primary-strong"
                : "border-b-transparent text-slate-600 hover:text-slate-900"
            }`}
          >
            {t(`esign.manager.views.${view}`)}
            {count !== undefined && (
              <span
                className={`rounded-full px-1.5 text-[11px] leading-5 ${
                  selected
                    ? "bg-[#EEF7F8] text-primary-strong"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/**
 * The whole management screen, always: figures, views, search and filters,
 * insights and the table. With nothing to show, the table body says why —
 * no requests yet, nothing matching the filters, or a list that failed to
 * load — and the rest of the screen stays where it is.
 */
export default function SignatureRequestsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { filters, setFilters, replaceFilters, clearFilters } =
    useTrackingFilters()
  const activeBranchId = useAppSelector(selectActiveBranchId) ?? undefined
  const options = useTrackingFilterOptions(activeBranchId)
  const canCountersign = Boolean(useCanCountersign())
  // One scope for everything on the screen: the rows, every count and the figures.
  const scoped = useMemo(
    () => ({
      ...queryFromFilters(filters),
      ...(activeBranchId ? { team: activeBranchId } : {})
    }),
    [filters, activeBranchId]
  )
  const listQuery = useMemo(
    () => queryForView(filters.view, scoped),
    [filters.view, scoped]
  )
  const list = useTrackingList(listQuery)
  const lifecycle = useEnvelopeLifecycle(list.reload)
  // "Needs my signature" is the company's turn, which only a countersigner can take.
  const views = useMemo(
    () =>
      MANAGER_VIEWS.filter(
        (view) =>
          (view !== "mine" || canCountersign) &&
          (view !== "drafts" || lifecycle.mayRaise)
      ),
    [canCountersign, lifecycle.mayRaise]
  )
  // The wizard closes back onto this page, which stayed mounted behind it: a
  // finished send says so, and everything is read from the server again.
  const refreshKey = (location.state as { refreshKey?: string } | null)
    ?.refreshKey
  // Only a new key asks again: `reload` never changes.
  const { reload } = list
  useEffect(() => {
    if (refreshKey) reload()
  }, [refreshKey, reload])
  const counts = useViewCounts(scoped, views, list.generation)
  const stats = useTrackingStats(activeBranchId, refreshKey)
  const summary = useTrackingSummary(listQuery, list.generation)
  const [activityFor, setActivityFor] = useState<TrackedEnvelope | null>(null)
  const [viewing, setViewing] = useState<TrackedEnvelope | null>(null)

  // The wizard opens over this page, and closing it comes back here.
  const here = `${location.pathname}${location.search}`
  const onAction = useCallback(
    (action: RowAction, row: TrackedEnvelope) => {
      if (action === "resume")
        navigate(RoutesPaths.esign.envelope(row.id), {
          state: { returnTo: here }
        })
      if (action === "reviewAndSign") navigate(RoutesPaths.esign.sign(row.id))
      if (action === "viewSigned") setViewing(row)
      if (action === "view") navigate(RoutesPaths.esign.request(row.id))
    },
    [navigate, here]
  )

  const actions = useMemo<ColumnsType<TrackedEnvelope>[number]>(
    () => ({
      title: t("esign.tracking.columns.actions"),
      key: "actions",
      width: 210,
      render: (_, row) => {
        const primary = primaryActionOf(row, { canCountersign })
        const sent = row.status !== "draft"
        const items = [
          ...(sent
            ? [{ key: "request", label: t("esign.manager.actions.view") }]
            : []),
          ...(sent
            ? [{ key: "view", label: t("esign.tracking.actions.view") }]
            : []),
          { key: "activity", label: t("esign.tracking.actions.activity") },
          ...(canCountersign && row.stage === "awaiting_countersignature"
            ? [
                {
                  key: "countersign",
                  label: t("esign.manager.actions.reviewAndSign")
                }
              ]
            : []),
          ...(lifecycle.mayResend(row.status)
            ? [{ key: "resend", label: t("esign.lifecycle.resend") }]
            : []),
          ...(lifecycle.mayVoid(row.status)
            ? [{ key: "void", label: t("esign.lifecycle.void"), danger: true }]
            : []),
          ...(lifecycle.mayDelete(row.status)
            ? [
                {
                  key: "delete",
                  label: t("esign.lifecycle.delete"),
                  danger: true
                }
              ]
            : [])
        ]
        return (
          <Flex justify={"end"} align={"center"} gap={8}>
            <Button
              size={"small"}
              type={primary === "reviewAndSign" ? "primary" : "default"}
              data-testid={`manager-primary-${row.id}`}
              onClick={() => onAction(primary, row)}
            >
              {t(`esign.manager.actions.${primary}`)}
            </Button>
            <RowActionsMenu
              items={items}
              buttonProps={
                { "data-testid": `tracking-row-actions-${row.id}` } as never
              }
              onClick={({ key }) => {
                if (key === "request")
                  navigate(RoutesPaths.esign.request(row.id))
                if (key === "view") setViewing(row)
                if (key === "activity") setActivityFor(row)
                if (key === "countersign")
                  navigate(RoutesPaths.esign.sign(row.id))
                if (key === "resend") void lifecycle.resend(row.id)
                if (key === "void")
                  lifecycle.startVoid(row.id, row.subject.name)
                if (key === "delete")
                  lifecycle.startDelete(row.id, row.subject.name)
              }}
            />
          </Flex>
        )
      }
    }),
    [t, navigate, lifecycle, canCountersign, onAction]
  )
  const onView = useCallback(
    (row: TrackedEnvelope) =>
      row.status === "draft" ? onAction("resume", row) : setViewing(row),
    [onAction]
  )
  const columns = useTrackingColumns({
    actions,
    ordering: filters.ordering,
    canCountersign,
    onView
  })
  const { tableRef, tableHeight, onTableScroll } = useScrolledTable({
    loading: list.loading,
    next: list.next,
    loadMore: list.loadMore,
    fullPage: true
  })
  const contentHeight = useTableContentHeight(tableRef)
  const height = contentHeight
    ? Math.min(tableHeight, contentHeight)
    : tableHeight

  const newRequest = lifecycle.mayRaise
    ? () =>
        navigate(RoutesPaths.esign.newEnvelope({ scope: "company" }), {
          state: { returnTo: here }
        })
    : null
  const narrowed = activeFilterCount(filters) > 0 || filters.view !== "all"

  const emptyText = list.failed ? (
    <Empty description={t("esign.tracking.empty.failed")}>
      <Button size={"small"} onClick={list.reload}>
        {t("esign.tracking.empty.retry")}
      </Button>
    </Empty>
  ) : narrowed ? (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={t("esign.tracking.empty.filtered")}
    >
      <Button
        size={"small"}
        onClick={() =>
          replaceFilters({ ...EMPTY_FILTERS, ordering: filters.ordering })
        }
      >
        {t("esign.tracking.filters.clear")}
      </Button>
    </Empty>
  ) : (
    <Flex vertical align={"center"} gap={6} className={"py-8"}>
      <span
        aria-hidden
        className={
          "flex size-10 items-center justify-center rounded-full bg-[#EEF7F8] text-lg text-primary-strong"
        }
      >
        <FileTextOutlined />
      </span>
      <Text strong className={"text-base"}>
        {t("esign.manager.emptyTitle")}
      </Text>
      <Text type={"secondary"}>{t("esign.manager.emptyBody")}</Text>
      {newRequest && (
        <Button className={"mt-2"} onClick={newRequest}>
          {t("esign.manager.newRequest")}
        </Button>
      )}
    </Flex>
  )

  useEffect(() => {
    generateDocumentTitle(t("esign.manager.title"))
  }, [t])

  return (
    <Flex vertical gap={"middle"} className={"p-4"}>
      <Flex justify={"space-between"} align={"center"} wrap gap={"small"}>
        <Flex vertical align={"flex-start"}>
          <Title level={3} className={"!mb-0 !text-left"}>
            EZ-<span className={"text-primary-strong"}>Sign</span>
          </Title>
          <Text type={"secondary"}>{t("esign.manager.subtitle")}</Text>
        </Flex>
        {newRequest && (
          <Button type={"primary"} icon={<PlusOutlined />} onClick={newRequest}>
            {t("esign.manager.newRequest")}
          </Button>
        )}
      </Flex>

      <WelcomeBanner />

      <TrackingKpiRow
        stats={stats}
        selected={filters.tile}
        onSelect={(tile) =>
          replaceFilters({ ...EMPTY_FILTERS, tile, ordering: filters.ordering })
        }
      />

      <section
        aria-label={t("esign.manager.workspaceLabel")}
        className={
          "overflow-hidden rounded-xl border border-solid border-slate-200 bg-white"
        }
      >
        <ViewTabs
          views={views}
          current={filters.view}
          counts={counts}
          onChange={(view) => setFilters({ view })}
        />

        <TrackingFiltersBar
          embedded
          filters={filters}
          setFilters={setFilters}
          clearFilters={clearFilters}
          senders={options.data?.senders ?? []}
          statusLocked={
            filters.view === "drafts" || filters.view === "completed"
          }
        />

        <Flex
          align={"center"}
          justify={"space-between"}
          gap={"small"}
          className={"px-4 pb-2 pt-4"}
        >
          <Flex align={"baseline"} gap={8}>
            <Text strong>{t("esign.manager.requestsHeading")}</Text>
            <Text type={"secondary"} className={"text-xs"}>
              {t("esign.manager.requestsCount", { count: list.count })}
            </Text>
          </Flex>
          {narrowed && (
            <Button
              type={"link"}
              size={"small"}
              onClick={() =>
                replaceFilters({ ...EMPTY_FILTERS, ordering: filters.ordering })
              }
            >
              {t("esign.tracking.filters.clear")}
            </Button>
          )}
        </Flex>

        <div
          ref={tableRef}
          className={"border-0 border-t border-solid border-t-slate-200"}
        >
          <EntityTable<TrackedEnvelope>
            surface={TABLE_SURFACES.esignTracking}
            tab={DEFAULT_TAB}
            columns={columns}
            dataSource={list.items}
            loading={list.loading && !list.items.length}
            rowKey={"id"}
            fitScrollX
            hideConfigButton
            withOutPagination
            pagination={false}
            locale={{ emptyText }}
            onScroll={onTableScroll}
            scroll={{ y: height }}
            onChange={(_pagination, _filters, sorter) => {
              const value = Array.isArray(sorter) ? sorter[0] : sorter
              const ordering = value?.order
                ? `${value.order === "descend" ? "-" : ""}${String(value.field)}`
                : null
              setFilters({ ordering: ordering as TrackingFilters["ordering"] })
            }}
          />
        </div>
        <div className={"border-0 border-t border-solid border-t-slate-200"}>
          <TableResultCount
            loaded={list.items.length}
            total={list.count}
            loading={list.loading}
            next={list.next}
            retryNext={list.retryNext}
            onRetry={list.loadMore}
          />
        </div>
      </section>

      <Collapse
        ghost
        defaultActiveKey={["insights"]}
        items={[
          {
            key: "insights",
            label: <Text strong>{t("esign.manager.insights")}</Text>,
            children: (
              <TrackingSummaryStrip
                summary={summary}
                onRetry={list.reload}
                scope={t(
                  activeFilterCount(filters) > 0
                    ? "esign.tracking.strip.scopeFiltered"
                    : "esign.tracking.strip.scope",
                  { view: t(`esign.manager.views.${filters.view}`) }
                )}
              />
            )
          }
        ]}
      />

      {activityFor && (
        <EnvelopeActivityDrawer
          envelope={activityFor}
          onClose={() => setActivityFor(null)}
        />
      )}
      {viewing && (
        <SignedDocumentDrawer
          envelope={viewing}
          onClose={() => setViewing(null)}
        />
      )}
      {lifecycle.dialog}
    </Flex>
  )
}
